/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert2';
import preview from '../../assets/preview.png';
import TextLine from '../../components/common/TextLine';
import TextBox from '../../components/common/TextBox';
import Dropdown from '../../components/common/Dropdown';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const MobileMenuBtn = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #1c1c1e;
    border-radius: 10px;
    color: #f5f5f7;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow-y: auto;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 28px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: 100vh;
    margin: 0;
    border-radius: 0;
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
`;

const SubText = styled.span`
  font-size: 13px;
  color: #86868b;
  margin-left: 12px;
  @media (max-width: 768px) { display: none; }
`;

const BtnGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 9px 22px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $variant }) => $variant === 'primary' ? '#007aff' : '#f3f4f6'};
  color: ${({ $variant }) => $variant === 'primary' ? '#fff' : '#374151'};
  &:hover {
    background: ${({ $variant }) => $variant === 'primary' ? '#0066d6' : '#e5e7eb'};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
`;

const CardTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 16px 0;
`;

const ThumbnailWrap = styled.label`
  position: relative;
  display: block;
  width: 100%;
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  &:hover > div { opacity: 1; }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #f5f5f7;
  border-radius: 12px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const PriceLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #6e6e73;
  width: 65px;
  text-transform: uppercase;
`;

const Won = styled.span`
  font-size: 14px;
  color: #86868b;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6e6e73;
  margin-bottom: 4px;
  margin-top: 12px;
  &:first-child { margin-top: 0; }
`;

const stockStatusOptions = [
  { value: 'normal', label: '정상' },
  { value: 'sold_out', label: '품절' },
  { value: 'discontinued', label: '단종' },
];

const StatusSelect = styled.select`
  width: 100%;
  padding: 7px 10px;
  border: 1.5px solid #d2d2d7;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
  &:focus { border-color: #007aff; }
`;

const priceFields = [
  { key: 'level1', label: 'Level 1' },
  { key: 'level2', label: 'Level 2' },
  { key: 'level3', label: 'Level 3' },
  { key: 'school', label: 'School' },
  { key: 'mall', label: 'Mall' },
  { key: 'vip', label: 'VIP' },
];

const ProductAddPage = () => {
  const navigate = useNavigate();

  const _handleChange = (e) => {
    let { value, name } = e.target;
    if (!(name === 'product_name' || name === 'description'))
      value = value.replace(/[^0-9]/g, '');
    setProduct({ ...product, [name]: value });
  };

  const _selectChange = (name, value) => {
    if (name === 'main_category') {
      getCategory(value);
    }
    setProduct({ ...product, [name]: value });
  };

  const [url, setUrl] = useState(preview);
  const [file, setFile] = useState();
  const [product, setProduct] = useState({
    main_category: '',
    sub_category: '',
    product_name: '',
    school: null,
    vip: null,
    level1: null,
    level2: null,
    level3: null,
    mall: null,
    description: '',
    img: '',
    stock_status: 'normal',
    stock_quantity: null,
  });
  const [manufacturer, setManufacturer] = useState([]);
  const [category, setCategory] = useState([]);

  const getManufacturer = async () => {
    try {
      const { data } = await axios.get('/admin/manufacturer');
      const arr = data.map((d) => d.main_category);
      setManufacturer(arr);
      getCategory(arr[0]);
    } catch (e) { console.log(e); }
  };

  const getCategory = async (mfr) => {
    try {
      const { data } = await axios.get(`/admin/category/${mfr}`);
      setCategory(data.map((d) => d.sub_category));
    } catch (e) { console.log(e); }
  };

  const _uploadImage = (e) => {
    const imgFile = e.target.files[0];
    setUrl(URL.createObjectURL(imgFile));
    setFile(imgFile);
  };

  const createProduct = async () => {
    if (
      url !== preview &&
      product.main_category &&
      product.sub_category &&
      product.product_name &&
      product.vip &&
      product.level1 &&
      product.level2 &&
      product.level3 &&
      product.school &&
      product.mall &&
      product.description
    ) {
      const result = await swal.fire({
        text: '제품을 등록하시겠습니까?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#007aff',
        cancelButtonColor: '#a3a3a3',
        confirmButtonText: '등록',
        cancelButtonText: '취소',
      });
      if (result.isConfirmed) {
        try {
          const res = await axios.post('/admin/products/productlist', product);
          if (res.status === 200) {
            const formData = new FormData();
            formData.append('image', file);
            await axios.post(`/admin/products/image/${res.data._id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            swal.fire({ text: '등록 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
            navigate('/product/list');
          }
        } catch {
          swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
        }
      }
    } else {
      swal.fire({ text: '입력하지 않은 값이 있습니다.', icon: 'warning', confirmButtonColor: '#007aff' });
    }
  };

  useEffect(() => { getManufacturer(); }, []);

  return (
    <div style={{ width: '100%' }}>
      <Container>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MobileMenuBtn onClick={() => window.dispatchEvent(new Event('open-sidebar'))}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </MobileMenuBtn>
            <Title>제품 등록</Title>
            <SubText>모든 항목을 입력 후 등록 버튼을 눌러주세요.</SubText>
          </div>
          <BtnGroup>
            <Btn onClick={() => navigate('/product/list')}>목록</Btn>
            <Btn $variant="primary" onClick={createProduct}>등록</Btn>
          </BtnGroup>
        </Header>

        <Grid>
          <Card>
            <CardTitle>썸네일</CardTitle>
            <ThumbnailWrap htmlFor="input-file-add">
              <ThumbnailOverlay>클릭하여 선택</ThumbnailOverlay>
              <Image src={url} />
            </ThumbnailWrap>
            <input id="input-file-add" type="file" accept="image/*" onChange={_uploadImage} style={{ display: 'none' }} />
          </Card>

          <Card>
            <CardTitle>가격 정보</CardTitle>
            {priceFields.map((f) => (
              <PriceRow key={f.key}>
                <PriceLabel>{f.label}</PriceLabel>
                <div style={{ flex: 1 }}>
                  <TextLine value={product[f.key]} name={f.key} onChange={_handleChange} />
                </div>
                <Won>&#8361;</Won>
              </PriceRow>
            ))}
          </Card>

          <Card>
            <CardTitle>제품 정보</CardTitle>
            <FieldLabel>제조사</FieldLabel>
            <Dropdown value={product.main_category || '선택하세요.'} name="main_category" data={manufacturer} _selectChange={_selectChange} />
            <FieldLabel>카테고리</FieldLabel>
            <Dropdown value={product.sub_category || '선택하세요.'} name="sub_category" data={category} _selectChange={_selectChange} />
            <FieldLabel>제품명</FieldLabel>
            <TextBox height={60} value={product.product_name} name="product_name" onChange={_handleChange} />
            <FieldLabel>판매 상태</FieldLabel>
            <StatusSelect
              value={product.stock_status}
              onChange={(e) => setProduct({ ...product, stock_status: e.target.value })}
            >
              {stockStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </StatusSelect>
            <FieldLabel>재고 수량</FieldLabel>
            <TextLine
              value={product.stock_quantity != null ? product.stock_quantity : ''}
              name="stock_quantity"
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setProduct({ ...product, stock_quantity: val === '' ? null : Number(val) });
              }}
              placeholder="미입력 시 재고 미관리"
            />
          </Card>
        </Grid>

        <Card style={{ width: '100%' }}>
          <CardTitle>상세 설명</CardTitle>
          <TextBox height={120} value={product.description} name="description" onChange={_handleChange} />
        </Card>
      </Container>
    </div>
  );
};
export default ProductAddPage;
